import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { AxiosError } from 'axios';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

/**
 * Get the current user's auth token
 */
async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error('No authentication token available');
  }

  return token;
}

/**
 * Create axios instance with auth header
 */
async function createAuthenticatedClient() {
  const token = await getAuthToken();

  return axios.create({
    baseURL: API_ENDPOINT,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

// ============================================
// Types
// ============================================

export interface ProductVariant {
  id: string;
  title: string;
  sku: string;
  price: string;
  wholesalePrice: string;
  inventoryQuantity: number;
  imageUrl?: string | null;
}

export interface Product {
  id: string;
  title: string;
  productType: string;
  tags: string[];
  imageUrl: string | null;
  variants: ProductVariant[];
  settings?: {
    wholesaleDiscountPercent: number;
    quantityDiscountThreshold: number;
    quantityDiscountAmount: number;
    quantityDiscountEnabled: boolean;
    minimumQuantity: number;
    hasCustomSettings: boolean;
  };
}

export interface ProductsResponse {
  success: boolean;
  products: Product[];
  productTypes: string[];
  count: number;
}

export interface OrderItem {
  variantId: string;
  productId: string;
  quantity: number;
}

export interface CreateOrderRequest {
  items: OrderItem[];
  note?: string;
}

export interface OrderLineItem {
  title: string;
  quantity: number;
  originalPrice: string;
  discountedPrice: string;
}

export interface CreatedOrder {
  id: string;
  name: string;
  invoiceUrl: string;
  totalPrice: string;
  lineItems: OrderLineItem[];
}

export interface CreateOrderResponse {
  success: boolean;
  message: string;
  order: CreatedOrder;
  customer: {
    id: string;
    email: string;
    isNew: boolean;
  };
}

export interface ApiError {
  error: string;
  message: string;
  status?: string;
}

// Order History Types
export interface MyOrderLineItem {
  title: string;
  quantity: number;
  originalPrice: string;
  discountedPrice: string;
  sku: string;
  variantId: string | null;
  productId: string | null;
}

export interface MyOrder {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  status: 'OPEN' | 'INVOICE_SENT' | 'COMPLETED';
  totalPrice: string;
  currency: string;
  lineItems: MyOrderLineItem[];
  invoiceUrl: string | null;
  invoiceSentAt: string | null;
}

export interface MyOrdersResponse {
  success: boolean;
  orders: MyOrder[];
  hasMore: boolean;
  endCursor: string | null;
}

// Display Case Types
export interface DisplayCaseInfo {
  caseId: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  dimensions: string;
}

export interface CustomerPurchase {
  cognitoSub: string;
  caseId: string;
  customerEmail: string;
  companyName: string;
  caseName: string;
  purchasePrice: number;
  status: 'requested' | 'approved' | 'active' | 'completed' | 'cancelled';
  totalRebateEarned: number;
  remainingBalance: number;
  requestedAt: string;
  updatedAt: string;
  rebates?: CustomerRebate[];
}

export interface CustomerRebate {
  orderId: string;
  orderName: string;
  orderTotal: number;
  rebatePercent: number;
  rebateAmount: number;
  cappedAmount: number;
  createdAt: string;
}

export interface DisplayCaseProgramResponse {
  success: boolean;
  programActive: boolean;
  rebatePercent: number;
  rebateDescription: string;
  cases: DisplayCaseInfo[];
}

export interface MyRebatesResponse {
  success: boolean;
  purchases: CustomerPurchase[];
  rebatePercent: number;
  programActive: boolean;
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch wholesale products from the API
 */
export async function getProducts(options?: { includeHidden?: boolean }): Promise<ProductsResponse> {
  try {
    const client = await createAuthenticatedClient();
    const params = options?.includeHidden ? { includeHidden: 'true' } : {};
    const response = await client.get<ProductsResponse>('/products', { params });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Account Pending Approval');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch products');
    }
    throw error;
  }
}

/**
 * Create a wholesale order (draft order in Shopify)
 */
export async function createOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.post<CreateOrderResponse>('/orders', request);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Account Pending Approval');
      }
      if (axiosError.response?.status === 400) {
        throw new Error(axiosError.response?.data?.message || 'Invalid order request');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to create order');
    }
    throw error;
  }
}

// ============================================
// Order History API Functions
// ============================================

export async function getMyOrders(after?: string): Promise<MyOrdersResponse> {
  try {
    const client = await createAuthenticatedClient();
    const params = after ? { after } : {};
    const response = await client.get<MyOrdersResponse>('/my-orders', { params });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Account Pending Approval');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch orders');
    }
    throw error;
  }
}

// ============================================
// Display Case API Functions
// ============================================

export async function getDisplayCaseProgram(): Promise<DisplayCaseProgramResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get<DisplayCaseProgramResponse>('/display-cases');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Account Pending Approval');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch display case program');
    }
    throw error;
  }
}

export async function requestDisplayCase(caseId: string): Promise<{ success: boolean; message: string }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.post('/display-cases/request', { caseId });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Account Pending Approval');
      throw new Error(axiosError.response?.data?.message || 'Failed to request display case');
    }
    throw error;
  }
}

export async function getMyRebates(): Promise<MyRebatesResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get<MyRebatesResponse>('/display-cases/my-rebates');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Account Pending Approval');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch rebate data');
    }
    throw error;
  }
}
